import React from "react";
import { Zap, Target, Gauge } from "lucide-react";

const SpeedVsAccuracySection = ({ speedAccuracyData }) => {
  const getZoneConfig = (zone) => {
    const configs = {
      balanced: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        icon: "✅",
        label: "Balanced",
      },
      fast: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        icon: "⚡",
        label: "Fast",
      },
      slow: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        icon: "🐢",
        label: "Slow",
      },
    };

    return configs[zone] || configs.balanced;
  };

  const zoneConfig = getZoneConfig(speedAccuracyData.zone);

  const accuracyData = speedAccuracyData?.trendData?.accuracy || [];
  const timeData = speedAccuracyData?.trendData?.timePerQuestion || [];
  const labels = speedAccuracyData?.trendData?.labels || [];

  const maxAccuracy = Math.max(...accuracyData, 1);
  const maxTime = Math.max(...timeData, 1);

  const efficiency = Math.round(
    (speedAccuracyData.metrics.accuracy /
      speedAccuracyData.metrics.avgTimePerQuestion) *
      10,
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900">Speed vs Accuracy</h2>

        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-md border ${zoneConfig.bg} ${zoneConfig.border}`}
        >
          <span>{zoneConfig.icon}</span>
          <span className={`text-xs font-medium ${zoneConfig.text}`}>
            {zoneConfig.label}
          </span>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-center">
          <Target className="w-4 h-4 text-emerald-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">
            {speedAccuracyData.metrics.accuracy}%
          </div>
          <div className="text-[11px] text-gray-600">Accuracy</div>
        </div>

        <div className="p-2.5 bg-orange-50 border border-orange-100 rounded-lg text-center">
          <Gauge className="w-4 h-4 text-orange-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">
            {speedAccuracyData.metrics.avgTimePerQuestion}s
          </div>
          <div className="text-[11px] text-gray-600">Time/Q</div>
        </div>

        <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg text-center">
          <Zap className="w-4 h-4 text-blue-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">{efficiency}</div>
          <div className="text-[11px] text-gray-600">Efficiency</div>
        </div>
      </div>

      {/* Trend Chart */}
      <div className="border border-gray-100 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-900">
            Accuracy vs Time
          </h3>

          <div className="flex gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span>Accuracy</span>
            </div>

            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <span>Time</span>
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between h-24 gap-2">
          {labels.map((label, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="flex items-end gap-1 h-20 w-full">
                <div
                  className="flex-1 bg-emerald-500 rounded-t"
                  style={{
                    height: `${
                      ((accuracyData[index] || 0) / maxAccuracy) * 100
                    }%`,
                  }}
                />

                <div
                  className="flex-1 bg-orange-500 rounded-t"
                  style={{
                    height: `${((timeData[index] || 0) / maxTime) * 100}%`,
                  }}
                />
              </div>

              <span className="text-[10px] text-gray-500 mt-1">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="text-sm font-medium text-emerald-900">
          {speedAccuracyData.title}
        </div>

        <p className="text-xs text-emerald-700 mt-1">
          {speedAccuracyData.description}
        </p>
      </div>
    </div>
  );
};

export default SpeedVsAccuracySection;
